import { useParams, Link } from "react-router-dom";
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ArrowLeft, MapPin, ExternalLink, BookOpen, ChevronDown } from "lucide-react";
import { getVenueBySlug } from "@/components/essex/venueData";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import Header from "@/components/essex/Header";
import Footer from "@/components/essex/Footer";
import { SecureDateCTA } from "@/components/essex/SecureDateCTA";

function VenueBlogPost({ post, index }) {
  const [expanded, setExpanded] = useState(false);
  const paragraphs = post.content.split("\n\n");

  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7, delay: index * 0.1 }}
      data-testid={`venue-blog-post-${index}`}
      className="border-l-2 border-[#C2A878]/20 pl-6 md:pl-10"
      itemScope
      itemType="https://schema.org/Article"
    >
      <h3 className="font-serif text-xl sm:text-2xl text-[#F5F5F0] tracking-tight leading-snug" itemProp="headline">
        {post.title}
      </h3>
      <div className="mt-4 space-y-4" itemProp="articleBody">
        <p className="text-sm md:text-base text-[#A19D94] font-sans leading-relaxed">
          {paragraphs[0]}
        </p>
        {paragraphs.slice(1).map((paragraph, j) => (
          <p
            key={j}
            className={`text-sm md:text-base text-[#A19D94] font-sans leading-relaxed ${!expanded ? "sr-only" : ""}`}
            aria-hidden={!expanded}
          >
            {paragraph}
          </p>
        ))}
      </div>
      {paragraphs.length > 1 && (
        <button
          onClick={() => setExpanded(!expanded)}
          data-testid={`venue-blog-toggle-${index}`}
          aria-expanded={expanded}
          className="mt-4 inline-flex items-center gap-1.5 text-sm text-[#C2A878] hover:text-[#d4bc92] transition-colors font-sans"
        >
          {expanded ? "Read less" : "Read more"}
          <ChevronDown
            size={14}
            className={`transition-transform duration-300 ${expanded ? "rotate-180" : ""}`}
          />
        </button>
      )}
      {expanded && <SecureDateCTA variant="minimal" />}
    </motion.article>
  );
}

export default function VenuePage() {
  const { slug } = useParams();
  const venue = getVenueBySlug(slug);

  if (!venue) {
    return (
      <div className="bg-[#0A0908] min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-serif text-3xl text-[#F5F5F0]">Venue not found</h1>
          <Link to="/" className="mt-4 inline-block text-[#C2A878] hover:underline font-sans text-sm">
            Back to Essex Wedding Photography & Video
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="venue-page" className="bg-[#0A0908] min-h-screen">
      <Helmet>
        <title>{`${venue.name} Wedding Photography and Videography | Wedding Video Essex`}</title>
        <meta name="description" content={`Wedding photography and videography at ${venue.name}, Essex. ${venue.description.split('.')[0]}. Watch our films, read our guides, and check your date.`} />
        <meta property="og:title" content={`${venue.name} Wedding Photography and Videography | Wedding Video Essex`} />
        <meta property="og:description" content={`See our wedding photography and videography work at ${venue.name}. Real films, real couples, real moments from one of Essex's finest venues.`} />
        <meta property="og:image" content={`https://essex.weddingvideo.co.uk/og/${venue.slug}.jpg`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${venue.name} Wedding Photography and Videography`} />
        <meta name="twitter:image" content={`https://essex.weddingvideo.co.uk/og/${venue.slug}.jpg`} />
        <link rel="canonical" href={`https://essex.weddingvideo.co.uk/${venue.slug}`} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://essex.weddingvideo.co.uk" },
            { "@type": "ListItem", "position": 2, "name": `${venue.name} Wedding Photography and Videography`, "item": `https://essex.weddingvideo.co.uk/${venue.slug}` }
          ]
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": `${venue.name} Wedding Photography and Videography`,
          "description": venue.description,
          "image": `https://essex.weddingvideo.co.uk/og/${venue.slug}.jpg`,
          "author": { "@type": "Organization", "name": "Wedding Video Essex" },
          "publisher": { "@type": "Organization", "name": "Wedding Video Essex", "url": "https://essex.weddingvideo.co.uk" },
          "mainEntityOfPage": `https://essex.weddingvideo.co.uk/${venue.slug}`
        })}</script>
      </Helmet>
      <Header />

      {/* Hero */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24">
        <div className="absolute inset-0">
          <img
            src={venue.image}
            alt={`${venue.name} wedding venue Essex`}
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0908]/70 via-[#0A0908]/60 to-[#0A0908]" />
        </div>

        <div className="relative max-w-5xl mx-auto px-6 md:px-12">
          <Link
            to="/"
            data-testid="venue-back-link"
            className="inline-flex items-center gap-2 text-xs tracking-[0.15em] uppercase text-[#A19D94] hover:text-[#C2A878] transition-colors font-sans mb-8"
          >
            <ArrowLeft size={14} /> Back to Essex Wedding Photography & Video
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="w-4 h-4 text-[#C2A878]" />
              <span className="text-xs tracking-[0.2em] uppercase text-[#C2A878] font-sans">
                Essex Wedding Venue
              </span>
            </div>
            <h1
              data-testid="venue-page-heading"
              className="font-serif text-3xl sm:text-4xl lg:text-5xl xl:text-6xl tracking-tighter font-light text-[#F5F5F0] leading-[1.1]"
            >
              {venue.name} <em className="italic text-[#C2A878]">Wedding Films & Photography</em>
            </h1>
            <p className="mt-6 text-base md:text-lg text-[#A19D94] font-sans leading-relaxed max-w-2xl">
              {venue.description}
            </p>
            {venue.website && (
              <a
                href={venue.website}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="venue-website-link"
                className="mt-4 inline-flex items-center gap-2 text-sm text-[#C2A878] hover:text-[#d4bc92] transition-colors font-sans"
              >
                Visit Venue Website <ExternalLink size={14} />
              </a>
            )}
          </motion.div>
        </div>
      </section>

      {/* Venue Photos Gallery */}
      {venue.photos.length > 1 && (
        <section data-testid="venue-photos-section" className="bg-[#0A0908] pb-16 md:pb-24">
          <div className="max-w-6xl mx-auto px-6 md:px-12">
            <p className="text-xs tracking-[0.2em] uppercase text-[#C2A878] font-sans mb-6">
              From Our Shoots
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {venue.photos.map((photo, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="aspect-[4/3] overflow-hidden border border-white/[0.06]"
                >
                  <img
                    src={photo}
                    alt={`${venue.name} wedding photography ${i + 1}`}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                </motion.div>
              ))}
            </div>

            {/* CTA after photos */}
            <div className="mt-8">
              <SecureDateCTA
                variant="inline"
                heading={`Getting married at ${venue.name}?`}
                subtext="Pop your date in and we'll check availability."
              />
            </div>
          </div>
        </section>
      )}

      {/* Wedding Films at this Venue */}
      <section data-testid="venue-films-section" className="bg-[#11100F] py-16 md:py-24" aria-label={`Wedding films at ${venue.name}`}>
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <p className="text-xs tracking-[0.2em] uppercase text-[#C2A878] font-sans mb-4">
            Our Work
          </p>
          <h2 className="font-serif text-2xl sm:text-3xl tracking-tight font-light text-[#F5F5F0] mb-8">
            Wedding Films at {venue.name}
          </h2>

          {venue.videos.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {venue.videos.map((video, i) => (
                <motion.div
                  key={video.id}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  data-testid={`venue-film-${i}`}
                  className="aspect-video overflow-hidden border border-white/[0.06]"
                >
                  <iframe
                    src={`https://www.youtube.com/embed/${video.id}?rel=0`}
                    title={video.title}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    loading="lazy"
                  />
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 border border-white/[0.06] bg-white/[0.02]">
              <p className="text-[#A19D94] font-sans text-sm">
                We've photographed and filmed at {venue.name} and are currently editing new content from this venue.
              </p>
              <p className="mt-2 text-[#A19D94]/60 font-sans text-xs">
                Check back soon or view our full portfolio on the{" "}
                <Link to="/" className="text-[#C2A878] hover:underline">main page</Link>.
              </p>
            </div>
          )}

          {/* CTA after films */}
          <div className="mt-8">
            <SecureDateCTA
              variant="inline"
              heading={`Love our ${venue.name} work?`}
              subtext="Let's create something just as good for your day."
            />
          </div>
        </div>
      </section>

      {/* Blog Posts */}
      {venue.blogPosts && venue.blogPosts.length > 0 && (
        <section data-testid="venue-blog-section" className="bg-[#0A0908] py-16 md:py-24" aria-label={`${venue.name} wedding photography and videography guides`}>
          <div className="max-w-3xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="mb-12"
            >
              <div className="flex items-center gap-2 mb-4">
                <BookOpen className="w-4 h-4 text-[#C2A878]" />
                <p className="text-xs tracking-[0.2em] uppercase text-[#C2A878] font-sans">
                  {venue.name} Guides
                </p>
              </div>
              <h2 className="font-serif text-2xl sm:text-3xl tracking-tight font-light text-[#F5F5F0]">
                Wedding photography & videography insights for {venue.name}
              </h2>
            </motion.div>

            <div className="space-y-16">
              {venue.blogPosts.map((post, i) => (
                <VenueBlogPost key={post.title} post={post} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Full width CTA before FAQ */}
      <SecureDateCTA
        heading={`Getting married at ${venue.name}?`}
        subtext={`We know ${venue.name} like the back of our hand. Pop your details in and we'll check if your date is free.`}
        venueName={venue.name}
      />

      {/* Venue-Specific FAQ */}
      {venue.faqs && venue.faqs.length > 0 && (
        <section data-testid="venue-faq-section" className="bg-[#11100F] py-16 md:py-24" aria-label={`Frequently asked questions about ${venue.name} weddings`}>
          <div className="max-w-3xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="mb-10"
            >
              <p className="text-xs tracking-[0.2em] uppercase text-[#C2A878] font-sans mb-4">
                {venue.name} FAQs
              </p>
              <h2 className="font-serif text-2xl sm:text-3xl tracking-tight font-light text-[#F5F5F0]">
                Common questions about wedding photography & videography at {venue.name}
              </h2>
            </motion.div>

            <Accordion type="single" collapsible>
              {venue.faqs.map((faq, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                >
                  <AccordionItem
                    value={`venue-faq-${i}`}
                    className="border-b border-[#2A2826]/50"
                    data-testid={`venue-faq-item-${i}`}
                  >
                    <AccordionTrigger
                      className="py-5 text-left font-sans text-sm md:text-base text-[#F5F5F0] hover:text-[#C2A878] hover:no-underline transition-colors"
                    >
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-[#A19D94] font-sans leading-relaxed pb-5">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                </motion.div>
              ))}
            </Accordion>

            {/* FAQ Schema */}
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  mainEntity: venue.faqs.map((faq) => ({
                    "@type": "Question",
                    name: faq.q,
                    acceptedAnswer: { "@type": "Answer", text: faq.a },
                  })),
                }),
              }}
            />
          </div>
        </section>
      )}

      {/* Final CTA */}
      <SecureDateCTA
        heading="Thinking about your wedding date?"
        subtext={`We'd love to hear about your plans for ${venue.name}. Pop your details in and we'll get back to you within 24 hours.`}
        venueName={venue.name}
      />

      <Footer />
    </div>
  );
}
